import React, { useState } from "react";
import PropTypes from "prop-types";

import ListarDatosPaginados from "../general/ListarDatosPaginados";
import { usePaginacion } from "../general/Paginacion";
import { BtnDetalle, BtnEditar } from "../general/BotonesAccion";
import DetalleSolicitud from "./DetalleSolicitud";
import EditarSolicitud from "./forms/EditarSolicitud";
import ModalCancelar from "./components/ModalCancelar";
import { Cargando, EstadoVacio } from "./components/Estados";
import FiltrosSolicitudes from "./components/FiltrosSolicitudes";

import { useConsulta } from "./hooks/useConsulta";
import { useDebounce } from "./hooks/useDebounce";
import { listarSolicitudes, obtenerMisFiltros } from "./services/bibliotecaApi";
import { puedeEditar } from "./model/permisos";
import { COLUMNAS_PROCESOS, filaPrincipalProcesos } from "./model/presentacion";
import { ESTADOS_POR_DEFECTO, colorDeEstado, tituloConEstados } from "./model/gestion";
import { SOLICITUDES } from "./catalogo";

const TODOS = "";

/**
 * Pestaña Procesos del solicitante: sus solicitudes en la misma tabla de
 * Gestion (paginada en el servidor, con buscador) y con los mismos filtros
 * arriba: tipo, estados (con cuantas hay en cada uno) y periodo. Arranca en
 * las que siguen en curso (solicitada, en revision, preparada) del periodo
 * vigente. Aqui nadie
 * ve las de otra persona, ni siquiera el administrador; eso es Gestion
 * Biblioteca. El backend aplica la misma regla.
 *
 * Cada fila tiene el libro para abrir su detalle, que es otra pantalla y
 * consulta lo que el listado no trae (estudiantes, material, historial), y
 * el lapiz para editarla mientras sigue solicitada. Editar es un modal: se
 * abre encima de donde se este.
 *
 * Filtros, pagina y busqueda viven aqui, asi que al volver del detalle se
 * sigue donde se estaba.
 */
const Solicitudes = ({ usuario, onMensaje }) => {
  // Cambiar `version` vuelve a consultar, para que los cambios recien
  // guardados se vean sin recargar la pagina.
  const [version, setVersion] = useState(0);
  const paginacion = usePaginacion();
  const { pagina, porPagina } = paginacion;
  const [texto, setTexto] = useState("");
  // Se consulta cuando se deja de escribir, no en cada tecla.
  const buscar = useDebounce(texto.trim(), 400);
  const [tipo, setTipo] = useState(TODOS);
  const [estado, setEstado] = useState(ESTADOS_POR_DEFECTO);
  // null: el periodo vigente, que llega con los filtros.
  const [periodoElegido, setPeriodoElegido] = useState(null);

  const { datos: filtros, error: errorFiltros } = useConsulta(
    () => obtenerMisFiltros({ tipo, periodo: periodoElegido }),
    [tipo, periodoElegido, version],
    null,
  );
  const periodo = periodoElegido ?? filtros?.vigente ?? "";

  // Se espera a saber el periodo vigente, para no listar primero sin el.
  // Mientras llega otra pagina, `datos` conserva la anterior.
  const { datos, cargando, error } = useConsulta(
    () =>
      filtros
        ? listarSolicitudes({ buscar, tipo, estado, periodo }, { pagina, porPagina })
        : Promise.resolve(null),
    [Boolean(filtros), buscar, tipo, estado, periodo, pagina, porPagina, version],
    null,
  );

  // Cambiar un filtro vuelve a la primera pagina.
  const filtrar = (cambiar) => (valor) => {
    cambiar(valor);
    paginacion.reiniciar();
  };

  const [abierta, setAbierta] = useState(null);
  const [editando, setEditando] = useState(null);
  const [cancelando, setCancelando] = useState(null);

  // Editar se abre igual desde la tabla y desde el detalle.
  const modalEdicion = editando && (
    <EditarSolicitud
      id={editando}
      usuario={usuario}
      onMensaje={onMensaje}
      onCerrar={() => setEditando(null)}
      onGuardada={() => {
        setEditando(null);
        setVersion((actual) => actual + 1);
      }}
    />
  );

  // El detalle reemplaza la tabla, como en Procesos Academicos.
  if (abierta) {
    return (
      <>
        <DetalleSolicitud
          id={abierta}
          usuario={usuario}
          version={version}
          volver={() => setAbierta(null)}
          onEditar={() => setEditando(abierta)}
          onCancelar={setCancelando}
        />
        {modalEdicion}
        {cancelando && (
          <ModalCancelar
            solicitud={cancelando}
            onMensaje={onMensaje}
            onCerrar={() => setCancelando(null)}
            onCancelada={() => {
              setCancelando(null);
              setVersion((actual) => actual + 1);
            }}
          />
        )}
      </>
    );
  }

  // Solo la primera carga ocupa la pantalla entera.
  if (errorFiltros || (error && !datos)) {
    return <EstadoVacio mensaje="No fue posible cargar las solicitudes" />;
  }
  if (!filtros || (cargando && !datos)) return <Cargando />;

  return (
    <div style={{ padding: "1% 2%" }}>
      <FiltrosSolicitudes
        tipos={SOLICITUDES}
        tipo={tipo}
        onTipo={filtrar(setTipo)}
        estado={estado}
        estados={filtros.estados}
        total={filtros.total}
        onEstado={filtrar(setEstado)}
        periodo={periodo}
        periodos={filtros.periodos}
        vigente={filtros.vigente}
        onPeriodo={filtrar(setPeriodoElegido)}
      />
      <ListarDatosPaginados
        datos={datos?.solicitudes ?? []}
        total={datos?.total ?? 0}
        {...paginacion}
        cargando={cargando}
        titulo={tituloConEstados("Mis solicitudes", filtros.estados, estado)}
        busqueda={texto}
        onBuscar={filtrar(setTexto)}
        onRefrescar={() => setVersion((actual) => actual + 1)}
        mensajeVacio="No tienes solicitudes con estos filtros"
        avatar={({ estado_actual }) => estado_actual?.nombre?.charAt(0).toUpperCase()}
        color_avatar={({ estado_actual }) => colorDeEstado(estado_actual?.codigo)}
        fila_principal={filaPrincipalProcesos}
        filas={COLUMNAS_PROCESOS}
        acciones={(solicitud) => (
          <div>
            <BtnDetalle
              callback={() => setAbierta(solicitud.id)}
              color="#01579b"
              texto="Abrir"
            />
            {/* El lapiz solo sale mientras la solicitud sigue solicitada. */}
            {puedeEditar(solicitud, usuario) && (
              <BtnEditar callback={() => setEditando(solicitud.id)} />
            )}
          </div>
        )}
      />

      {modalEdicion}
    </div>
  );
};

Solicitudes.propTypes = {
  usuario: PropTypes.object,
  onMensaje: PropTypes.func,
};

Solicitudes.defaultProps = {
  onMensaje: () => {},
};

export default Solicitudes;
